/**
 * Knowledge panel — v0M9.5 (2026-07-24).
 *
 * Faction-wide research modal. Opened via the top-bar Knowledge button
 * (see `top-bar.ts`) or the `K` hotkey. Reuses the su-city-panel-*
 * CSS shell so the visual language stays consistent with the Settlement
 * panel + Army Info panel.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │  Knowledge Tree                                 [×] │
 *   ├─────────────────────────────────────────────────────┤
 *   │  ▓▓▓▓▓░░░░░ Researching: Fortify — 12 days remaining│
 *   ├─────────────────────────────────────────────────────┤
 *   │  ▸ Strategic Warfare                                │
 *   │      ○ Strategic Theory - Fortify   250 L  [Research]│
 *   │      ● Strategic Theory - Ambush   500 L  [Locked]  │
 *   │  ▸ Tactical Warfare                                 │
 *   │      ...                                            │
 *   └─────────────────────────────────────────────────────┘
 *
 * Rebuild discipline: DOM tree is built ONCE on mount (34 knowledges is
 * cheap). Per-frame sync() only mutates status + button labels — no
 * innerHTML thrash.
 */

import { injectMenuStyles } from './menu-styles';
import { loadKnowledge, groupKnowledgeByCategory } from '../../data/knowledge';
import type { Command } from '../../sim/commands';
import { SECONDS_PER_TICK, TICKS_PER_GAME_DAY, type FactionId, type GameState } from '../../sim/state';
import { computeFactionIncome } from '../../sim/economy';
import { RESEARCH_QUEUE_CAP } from '../../sim/apply-commands';

const KNOWLEDGE = loadKnowledge();
const CATEGORY_GROUPS = groupKnowledgeByCategory(KNOWLEDGE);

// Category display order — mirrors Michael's xlsx authoring order so
// tabs sort intuitively. Categories not listed fall to the bottom in
// insertion order (defensive if he adds a new category).
const CATEGORY_ORDER: readonly string[] = [
  'Strategic Warfare',
  'Tactical Warfare',
  'Logistics',
  'Settlements',
  'Engineering',
  'Espionage',
  'Maritime',
];

function sortedCategories(): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of CATEGORY_ORDER) {
    if (CATEGORY_GROUPS[c]) {
      out.push(c);
      seen.add(c);
    }
  }
  for (const c of Object.keys(CATEGORY_GROUPS)) {
    if (!seen.has(c)) out.push(c);
  }
  return out;
}

export interface KnowledgePanelCallbacks {
  onIssue(cmd: Command): void;
  onClose(): void;
}

export interface KnowledgePanelHandle {
  sync(state: GameState, isOpen: boolean, playerFactionId: FactionId | null): void;
  destroy(): void;
}

interface KnowledgeRow {
  container: HTMLElement;
  statusIcon: HTMLElement;
  costLabel: HTMLElement;
  button: HTMLButtonElement;
  name: string;
  /** v0M6.1 (2026-07-26) — Parent knowledge name. Empty for tier-1 roots. */
  parentName: string;
  /**
   * v0M6.1 — SVG path element rendering the L-shaped connector from
   * parent to this row's status icon. Null for tier-1 roots (no
   * parent to connect from) and until layoutConnectors() has run
   * (first panel open sizes the section, so paths are painted then).
   */
  edgePath: SVGPathElement | null;
  /**
   * v0M6.1 — Section container the row lives inside. Used at layout
   * time to look up sibling rows by parentName (chains never cross
   * categories per knowledge-tree schema — single-parent, same-category).
   */
  section: HTMLElement;
}

/**
 * v0M6.1 (2026-07-26) — Per-category section state. Wraps the category
 * header + its knowledge rows in a `position: relative` container with
 * an absolute-positioned SVG overlay used for prereq connector lines.
 */
interface KnowledgeSection {
  container: HTMLElement;
  svg: SVGSVGElement;
  rows: KnowledgeRow[];
}

export function mountKnowledgePanel(
  root: HTMLElement,
  callbacks: KnowledgePanelCallbacks,
): KnowledgePanelHandle {
  injectMenuStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'su-city-panel-backdrop';
  backdrop.style.display = 'none';
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) callbacks.onClose();
  });

  const panel = document.createElement('div');
  panel.className = 'su-city-panel';
  // Widen the panel — the knowledge grid needs more horizontal room than
  // the settlement panel default (520px).
  panel.style.minWidth = '640px';
  panel.style.maxWidth = '92vw';
  backdrop.appendChild(panel);

  // Header — title + close button.
  const header = document.createElement('div');
  header.className = 'su-city-panel-header';
  const title = document.createElement('h2');
  title.className = 'su-city-panel-title';
  title.textContent = 'Knowledge Tree';
  header.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'su-city-panel-close';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close (Esc)';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Body scroll container.
  const body = document.createElement('div');
  body.className = 'su-city-panel-body';
  panel.appendChild(body);

  // v0M6.2 (2026-07-26) — shared rich-detail popover. Single element
  // reused across every row-hover instead of one popover per row.
  // Appended to `document.body` (not the panel) so its viewport-edge
  // clamping isn't constrained by the panel's overflow-hidden and it
  // renders above the panel via a high z-index. Populated by
  // `showPopoverFor(row, spec)` on mouseenter; hidden on mouseleave.
  const popover = document.createElement('div');
  popover.style.position = 'fixed';
  popover.style.display = 'none';
  popover.style.zIndex = '10000';
  popover.style.maxWidth = '360px';
  popover.style.background = 'linear-gradient(180deg, #1a1610 0%, #14110b 100%)';
  popover.style.border = '1px solid #6a5a36';
  popover.style.borderRadius = '4px';
  popover.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.55)';
  popover.style.padding = '10px 12px';
  popover.style.pointerEvents = 'none';
  popover.style.color = '#e6dcc4';
  popover.style.fontSize = '12px';
  popover.style.lineHeight = '1.4';
  document.body.appendChild(popover);

  // Active-research banner — hidden when no active research; when active,
  // shows a progress bar + "N days remaining".
  // v0M6.3 (2026-07-26): a cancel-X sits top-right and dispatches
  // `cancel_active_research`. NO Learning refund on cancel per the
  // session-33 elicited policy — the button `title` states this so
  // players know before clicking.
  const activeBanner = document.createElement('div');
  activeBanner.style.position = 'relative';
  activeBanner.style.padding = '10px 12px';
  activeBanner.style.marginBottom = '12px';
  activeBanner.style.background = 'rgba(196, 168, 116, 0.08)';
  activeBanner.style.border = '1px solid #6a5a36';
  activeBanner.style.borderRadius = '4px';
  activeBanner.style.display = 'none';
  const activeLabel = document.createElement('div');
  activeLabel.style.fontSize = '12px';
  activeLabel.style.marginBottom = '6px';
  activeLabel.style.color = '#f4d47a';
  activeLabel.style.paddingRight = '20px'; // clear the cancel-X
  activeBanner.appendChild(activeLabel);
  const activeBarOuter = document.createElement('div');
  activeBarOuter.style.height = '8px';
  activeBarOuter.style.background = 'rgba(255, 255, 255, 0.10)';
  activeBarOuter.style.borderRadius = '4px';
  activeBarOuter.style.overflow = 'hidden';
  const activeBarFill = document.createElement('div');
  activeBarFill.style.height = '100%';
  activeBarFill.style.background = '#f4d47a';
  activeBarFill.style.width = '0%';
  activeBarFill.style.transition = 'width 200ms linear';
  activeBarOuter.appendChild(activeBarFill);
  activeBanner.appendChild(activeBarOuter);
  const activeCancelBtn = document.createElement('button');
  activeCancelBtn.textContent = '×'; // ×
  activeCancelBtn.title = 'Cancel research (Learning already spent is NOT refunded)';
  activeCancelBtn.style.position = 'absolute';
  activeCancelBtn.style.top = '4px';
  activeCancelBtn.style.right = '4px';
  activeCancelBtn.style.background = 'transparent';
  activeCancelBtn.style.border = 'none';
  activeCancelBtn.style.color = '#c4a874';
  activeCancelBtn.style.fontSize = '18px';
  activeCancelBtn.style.lineHeight = '1';
  activeCancelBtn.style.cursor = 'pointer';
  activeCancelBtn.style.padding = '2px 6px';
  activeCancelBtn.addEventListener('click', () => {
    if (lastState === null || lastPlayerFactionId === null) return;
    callbacks.onIssue({
      type: 'cancel_active_research',
      tick: lastState.tick,
      playerFactionId: lastPlayerFactionId,
    });
  });
  activeBanner.appendChild(activeCancelBtn);
  body.appendChild(activeBanner);

  // v0M6.3 (2026-07-26) — Queue section. One row per queued knowledge,
  // in order, each with a cancel-X. Header shows "Queue (N / CAP)".
  // Container is a `su-city-panel-row`-shaped strip; empty state hides
  // the whole section. Rebuilt on every sync() for simplicity — a
  // queue has ≤ RESEARCH_QUEUE_CAP rows (5 default), rebuild cost is
  // negligible.
  const queueSection = document.createElement('div');
  queueSection.style.marginBottom = '12px';
  queueSection.style.display = 'none';
  const queueHeader = document.createElement('div');
  queueHeader.style.fontSize = '11px';
  queueHeader.style.color = '#c4a874';
  queueHeader.style.textTransform = 'uppercase';
  queueHeader.style.letterSpacing = '0.04em';
  queueHeader.style.marginBottom = '4px';
  queueSection.appendChild(queueHeader);
  const queueList = document.createElement('div');
  queueList.style.display = 'flex';
  queueList.style.flexDirection = 'column';
  queueList.style.gap = '4px';
  queueSection.appendChild(queueList);
  body.appendChild(queueSection);

  // Idle note — shown when no active research + no research completed yet.
  const idleNote = document.createElement('div');
  idleNote.style.padding = '10px 12px';
  idleNote.style.marginBottom = '12px';
  idleNote.style.fontSize = '12px';
  idleNote.style.color = '#a3906a';
  idleNote.style.fontStyle = 'italic';
  idleNote.textContent = 'No active research. Pick a knowledge below to begin.';
  idleNote.style.display = 'none';
  body.appendChild(idleNote);

  // Build one section per category. Each section = category header +
  // list of knowledge rows.
  //
  // v0M6.1 (2026-07-26): each category is wrapped in a
  // `position: relative` container so we can overlay an SVG that draws
  // L-shaped prereq connector lines between rows. Rows still render in
  // normal DOM flow; the SVG sits behind them (via lower z-index) and
  // is repositioned on layout / resize by `layoutConnectors()`.
  const rows: KnowledgeRow[] = [];
  const sections: KnowledgeSection[] = [];
  for (const category of sortedCategories()) {
    const sectionDiv = document.createElement('div');
    sectionDiv.style.position = 'relative';
    sectionDiv.style.marginBottom = '4px';
    body.appendChild(sectionDiv);

    // SVG overlay lives BEHIND rows so labels/buttons stay clickable.
    // Rows use the default z-index (auto) which stacks above z-index: 0
    // siblings, so we set the svg to z-index: 0 explicitly + rows are
    // fine as-is.
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.left = '0';
    svg.style.top = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none';
    svg.style.zIndex = '0';
    sectionDiv.appendChild(svg);

    const catHeader = document.createElement('div');
    catHeader.style.fontSize = '13px';
    catHeader.style.color = '#c4a874';
    catHeader.style.textTransform = 'uppercase';
    catHeader.style.letterSpacing = '0.04em';
    catHeader.style.margin = '12px 0 6px 0';
    catHeader.style.borderBottom = '1px solid #4a3d24';
    catHeader.style.paddingBottom = '4px';
    catHeader.style.position = 'relative'; // stays above svg
    catHeader.style.zIndex = '1';
    catHeader.textContent = category;
    sectionDiv.appendChild(catHeader);

    const sectionRows: KnowledgeRow[] = [];

    const specs = CATEGORY_GROUPS[category] ?? [];
    // Sort by level to render chain in tier order (xlsx order should match
    // but be defensive).
    const sortedSpecs = [...specs].sort((a, b) => a.level - b.level);
    for (const spec of sortedSpecs) {
      // Look up the knowledge name — CATEGORY_GROUPS strips the key, so
      // reverse-lookup via the raw dict.
      const name = Object.keys(KNOWLEDGE).find((k) => KNOWLEDGE[k] === spec);
      if (!name) continue;

      const rowDiv = document.createElement('div');
      rowDiv.className = 'su-city-panel-row';
      rowDiv.style.paddingLeft = `${16 + (spec.level - 1) * 12}px`; // indent by tier
      rowDiv.style.position = 'relative'; // stays above svg overlay
      rowDiv.style.zIndex = '1';
      rowDiv.style.background = 'rgba(15, 12, 6, 0.72)'; // masks the connector line so it doesn't bleed through the row's own row
      // v0M6.2 (2026-07-26) — native `title` tooltip retired in favour of
      // the rich hover popover. Hover listeners wired below after the
      // row entry is constructed.

      // Status icon — filled circle when researched, open circle otherwise.
      // Also colour-codes prereq state (checked in sync).
      const statusIcon = document.createElement('div');
      statusIcon.textContent = '○';
      statusIcon.style.minWidth = '18px';
      statusIcon.style.fontSize = '14px';
      statusIcon.style.color = '#a3906a';
      rowDiv.appendChild(statusIcon);

      // Level badge.
      const levelBadge = document.createElement('div');
      levelBadge.textContent = `L${spec.level}`;
      levelBadge.style.minWidth = '24px';
      levelBadge.style.fontSize = '11px';
      levelBadge.style.color = '#8a7a52';
      levelBadge.style.marginRight = '6px';
      rowDiv.appendChild(levelBadge);

      // Name + effect (two-line).
      const nameBlock = document.createElement('div');
      nameBlock.className = 'su-city-panel-item-name';
      nameBlock.style.flex = '1';
      const nameDiv = document.createElement('div');
      nameDiv.textContent = name;
      nameBlock.appendChild(nameDiv);
      if (spec.effects) {
        const effectDiv = document.createElement('div');
        effectDiv.textContent = spec.effects;
        effectDiv.style.fontSize = '10px';
        effectDiv.style.opacity = '0.75';
        effectDiv.style.marginTop = '2px';
        nameBlock.appendChild(effectDiv);
      }
      rowDiv.appendChild(nameBlock);

      // Cost.
      const costLabel = document.createElement('div');
      costLabel.className = 'su-city-panel-item-cost';
      costLabel.textContent = `${spec.learningCost}L · ${spec.researchDays}d`;
      rowDiv.appendChild(costLabel);

      // Research button.
      const btn = document.createElement('button');
      btn.className = 'su-city-panel-action';
      btn.textContent = 'Research';
      const capturedName = name;
      btn.addEventListener('click', () => {
        callbacks.onIssue({
          type: 'start_research',
          tick: 0, // sim stamps from state.tick
          playerFactionId: 0 as unknown as FactionId, // sync() rewires; see below
          knowledgeName: capturedName,
        });
      });
      // We cannot capture the *current* playerFactionId at mount time, so
      // the button handler above patches a placeholder. sync() overwrites
      // the handler each frame with the real player id closure.
      rowDiv.appendChild(btn);

      sectionDiv.appendChild(rowDiv);
      const rowEntry: KnowledgeRow = {
        container: rowDiv,
        statusIcon,
        costLabel,
        button: btn,
        name,
        parentName: spec.prerequisite,
        edgePath: null,
        section: sectionDiv,
      };
      rows.push(rowEntry);
      sectionRows.push(rowEntry);

      // v0M6.2 — hover shows rich popover; leave hides it. Positioning
      // + content pulls from `lastState` captured on the most recent
      // sync() so income projections reflect current gameplay.
      const hoverName = name;
      rowDiv.addEventListener('mouseenter', () => showPopoverFor(hoverName, rowDiv));
      rowDiv.addEventListener('mouseleave', () => hidePopover());
    }
    sections.push({ container: sectionDiv, svg, rows: sectionRows });
  }

  // v0M6.1 (2026-07-26) — connector-line layout.
  //
  // For each row with a non-empty parent, look up the parent row in the
  // same section, then paint an L-shaped path from the parent's
  // status-icon center DOWN + OVER + INTO the child's status-icon
  // center. Path is (parentX, parentY+halfH) → (parentX, midY) →
  // (childX-8, midY) → (childX-8, childY) → (childX, childY). Runs on
  // first panel open + on window resize; per-line stroke colour is
  // updated per sync() based on researched / active / locked state.
  function iconCenter(row: KnowledgeRow): { x: number; y: number } {
    const secRect = row.section.getBoundingClientRect();
    const iconRect = row.statusIcon.getBoundingClientRect();
    return {
      x: iconRect.left - secRect.left + iconRect.width / 2,
      y: iconRect.top - secRect.top + iconRect.height / 2,
    };
  }

  function layoutConnectors(): void {
    for (const section of sections) {
      // Size the svg to the section's rendered box.
      const rect = section.container.getBoundingClientRect();
      section.svg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
      section.svg.setAttribute('width', String(rect.width));
      section.svg.setAttribute('height', String(rect.height));

      // Rebuild the path children — cheap for <10 rows/section.
      while (section.svg.firstChild) section.svg.removeChild(section.svg.firstChild);

      for (const row of section.rows) {
        row.edgePath = null;
        if (row.parentName === '') continue;
        const parent = section.rows.find((r) => r.name === row.parentName);
        if (!parent) continue;
        const p = iconCenter(parent);
        const c = iconCenter(row);
        // L-shape: down from parent, over, into child. Corner sits half
        // the vertical gap up from the child so the horizontal leg
        // aligns with the child's icon.
        const cornerX = Math.max(p.x, c.x - 10);
        const d = `M ${p.x} ${p.y + 8} L ${p.x} ${c.y} L ${c.x - 8} ${c.y}`;
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', d);
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', '#5a4a30'); // dim default; sync() overrides
        path.setAttribute('stroke-width', '1.5');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        section.svg.appendChild(path);
        row.edgePath = path;
        // cornerX is currently informational — the M/L path uses p.x
        // for the vertical leg since chains are always parent-tier <
        // child-tier so p.x < c.x holds by construction. Kept the
        // variable to make the layout intent explicit for the reader.
        void cornerX;
      }
    }
  }

  root.appendChild(backdrop);

  // v0M6.2 (2026-07-26) — popover state + helpers.
  //
  // `lastState` + `lastPlayerFactionId` are captured on each sync() so
  // the popover can compute up-to-date learning-income projections at
  // hover time. If sync() has never fired, hover still works — just
  // without projection lines.
  let lastState: GameState | null = null;
  let lastPlayerFactionId: FactionId | null = null;

  function walkPrereqChain(name: string): string[] {
    const chain: string[] = [];
    let cursor: string | undefined = KNOWLEDGE[name]?.prerequisite;
    // Cap at 10 to guard against a bad-data cycle (schema is single-parent + tiered so cycles shouldn't exist).
    for (let i = 0; cursor && cursor !== '' && i < 10; i++) {
      chain.push(cursor);
      cursor = KNOWLEDGE[cursor]?.prerequisite;
    }
    return chain;
  }

  function formatProjection(spec: (typeof KNOWLEDGE)[string]): string {
    if (lastState === null || lastPlayerFactionId === null) {
      return 'Enter a scenario to see projection.';
    }
    const faction = lastState.factions.get(lastPlayerFactionId);
    if (!faction) return 'Unknown faction.';
    const have = faction.resources.learning;
    if (have >= spec.learningCost) {
      return `Ready to research — ${spec.researchDays} days to complete.`;
    }
    const gap = spec.learningCost - have;
    const income = computeFactionIncome(lastState, lastPlayerFactionId); // per-second
    const learningPerDay = income.learning * SECONDS_PER_TICK * TICKS_PER_GAME_DAY;
    if (learningPerDay <= 0) {
      return 'No Learning income — build a research structure first.';
    }
    const daysToSave = Math.ceil(gap / learningPerDay);
    const total = daysToSave + spec.researchDays;
    return `~${daysToSave}d to save ${spec.learningCost}L (income ${learningPerDay.toFixed(1)}L/day), then ${spec.researchDays}d research — ~${total}d total.`;
  }

  function showPopoverFor(name: string, anchor: HTMLElement): void {
    const spec = KNOWLEDGE[name];
    if (!spec) return;

    // Populate.
    const chain = walkPrereqChain(name);
    const chainStr = chain.length === 0
      ? '<span style="color: #8a7a52;">None (tier-1 root)</span>'
      : chain.reverse().map((c) => escapeHtml(c)).join(' <span style="color: #6a5a36;">&rarr;</span> ');

    popover.innerHTML = `
      <div style="font-size: 14px; font-weight: bold; color: #f4d47a; margin-bottom: 2px;">${escapeHtml(name)}</div>
      <div style="font-size: 11px; color: #a3906a; margin-bottom: 8px;">${escapeHtml(spec.category)} &middot; Tier ${spec.level}</div>
      <div style="margin-bottom: 8px;">${escapeHtml(spec.description)}</div>
      <div style="border-top: 1px solid #4a3d24; padding-top: 6px; margin-bottom: 6px;">
        <div style="color: #c4a874; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px;">Effect</div>
        <div>${escapeHtml(spec.effects) || '<span style="color: #8a7a52;">(informational)</span>'}</div>
      </div>
      <div style="border-top: 1px solid #4a3d24; padding-top: 6px; margin-bottom: 6px;">
        <div style="color: #c4a874; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px;">Cost</div>
        <div>${spec.learningCost} Learning &middot; ${spec.researchDays} days</div>
      </div>
      <div style="border-top: 1px solid #4a3d24; padding-top: 6px; margin-bottom: 6px;">
        <div style="color: #c4a874; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px;">Prerequisites</div>
        <div>${chainStr}</div>
      </div>
      <div style="border-top: 1px solid #4a3d24; padding-top: 6px;">
        <div style="color: #c4a874; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px;">Projection</div>
        <div style="color: #d5c48f;">${escapeHtml(formatProjection(spec))}</div>
      </div>
    `;

    // Position. Show first so we can measure; then choose side.
    popover.style.display = 'block';
    const anchorRect = anchor.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: prefer right of anchor; flip to left if it clips.
    let left = anchorRect.right + margin;
    if (left + popRect.width > vw - 4) {
      left = anchorRect.left - margin - popRect.width;
    }
    // Fallback if still clipping (very narrow viewport): clamp to left edge.
    if (left < 4) left = 4;

    // Vertical: align to anchor top; clamp to viewport.
    let top = anchorRect.top;
    if (top + popRect.height > vh - 4) top = vh - popRect.height - 4;
    if (top < 4) top = 4;

    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }

  function hidePopover(): void {
    popover.style.display = 'none';
  }

  // Tiny HTML escaper for interpolated strings — knowledge names /
  // descriptions come from xlsx, but defensive against future authoring
  // that includes `<` `>` `&` characters.
  function escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // v0M6.1 (2026-07-26) — connector lines need positions relative to
  // each section's rendered box; those bounds are 0 while the backdrop
  // is `display: none`. Layout runs on the first sync() that transitions
  // to open, and again on window resize while the panel is open.
  let panelWasOpen = false;
  let laidOutAtLeastOnce = false;

  const onResize = (): void => {
    if (panelWasOpen) layoutConnectors();
  };
  window.addEventListener('resize', onResize);

  function sync(state: GameState, isOpen: boolean, playerFactionId: FactionId | null): void {
    // v0M6.2 (2026-07-26) — cache state + faction id so the hover
    // popover's projection lines pull live values without needing
    // sync-per-hover plumbing.
    lastState = state;
    lastPlayerFactionId = playerFactionId;

    if (!isOpen || playerFactionId === null) {
      backdrop.style.display = 'none';
      panelWasOpen = false;
      hidePopover();
      return;
    }
    backdrop.style.display = 'flex';
    // Panel became visible this frame — measure + paint connectors.
    // Deferred to next tick so the browser can lay out the flexbox
    // first (BCR on a just-shown element sometimes returns pre-layout
    // sizes; requestAnimationFrame is the standard escape hatch).
    if (!panelWasOpen || !laidOutAtLeastOnce) {
      panelWasOpen = true;
      requestAnimationFrame(() => {
        layoutConnectors();
        laidOutAtLeastOnce = true;
      });
    }
    const faction = state.factions.get(playerFactionId);
    if (!faction) return;
    const researched = new Set(faction.researchedKnowledge ?? []);
    const active = faction.activeResearch ?? null;
    const learning = Math.floor(faction.resources.learning);

    // v0M6.3 (2026-07-26) — snapshot the queue for this sync pass.
    // Optional field per state.ts back-compat; treat undefined as empty.
    const queue = faction.researchQueue ?? [];
    const queuedSet = new Set(queue);
    const queueFull = queue.length >= RESEARCH_QUEUE_CAP;

    // Active-research banner.
    if (active !== null) {
      const spec = KNOWLEDGE[active.knowledgeName];
      if (spec) {
        const total = spec.researchDays;
        const done = Math.min(active.progressDays, total);
        const pct = total > 0 ? (done / total) * 100 : 0;
        activeBarFill.style.width = `${pct.toFixed(1)}%`;
        const remaining = Math.max(0, total - done);
        activeLabel.textContent = `Researching: ${active.knowledgeName} — ${remaining.toFixed(1)} days remaining`;
        activeBanner.style.display = '';
        idleNote.style.display = 'none';
      }
    } else {
      activeBanner.style.display = 'none';
      // Show the idle note only if there's nothing active AND nothing
      // queued AND nothing has ever been researched — otherwise the
      // panel already communicates state.
      idleNote.style.display = researched.size === 0 && queue.length === 0 ? '' : 'none';
    }

    // v0M6.3 — Queue section. Rebuilt on each sync for simplicity
    // (≤ RESEARCH_QUEUE_CAP items; negligible cost). Rows are
    // `#N. KnowledgeName · Nd  [×]`; cancel-X dispatches
    // cancel_queued_research. Hidden when queue is empty.
    while (queueList.firstChild) queueList.removeChild(queueList.firstChild);
    if (queue.length > 0) {
      queueSection.style.display = '';
      queueHeader.textContent = `Queue (${queue.length} / ${RESEARCH_QUEUE_CAP})`;
      for (let i = 0; i < queue.length; i++) {
        const qName = queue[i]!;
        const qSpec = KNOWLEDGE[qName];
        const rowEl = document.createElement('div');
        rowEl.style.display = 'flex';
        rowEl.style.alignItems = 'center';
        rowEl.style.gap = '8px';
        rowEl.style.padding = '4px 8px';
        rowEl.style.background = 'rgba(196, 168, 116, 0.04)';
        rowEl.style.border = '1px solid #4a3d24';
        rowEl.style.borderRadius = '3px';
        const posEl = document.createElement('div');
        posEl.textContent = `#${i + 1}`;
        posEl.style.minWidth = '22px';
        posEl.style.color = '#c4a874';
        posEl.style.fontSize = '11px';
        rowEl.appendChild(posEl);
        const nameEl = document.createElement('div');
        nameEl.textContent = qName;
        nameEl.style.flex = '1';
        nameEl.style.color = '#e6dcc4';
        rowEl.appendChild(nameEl);
        if (qSpec) {
          const costEl = document.createElement('div');
          costEl.textContent = `${qSpec.learningCost}L · ${qSpec.researchDays}d`;
          costEl.style.fontSize = '11px';
          costEl.style.color = '#8a7a52';
          rowEl.appendChild(costEl);
        }
        const cancelEl = document.createElement('button');
        cancelEl.textContent = '×';
        cancelEl.title = 'Remove from queue';
        cancelEl.style.background = 'transparent';
        cancelEl.style.border = 'none';
        cancelEl.style.color = '#c4a874';
        cancelEl.style.fontSize = '16px';
        cancelEl.style.cursor = 'pointer';
        cancelEl.style.padding = '0 4px';
        const capturedQName = qName;
        cancelEl.addEventListener('click', () => {
          callbacks.onIssue({
            type: 'cancel_queued_research',
            tick: state.tick,
            playerFactionId,
            knowledgeName: capturedQName,
          });
        });
        rowEl.appendChild(cancelEl);
        queueList.appendChild(rowEl);
      }
    } else {
      queueSection.style.display = 'none';
    }

    // Per-row status + button state.
    for (const row of rows) {
      const spec = KNOWLEDGE[row.name]!;
      const isResearched = researched.has(row.name);
      const isActive = active !== null && active.knowledgeName === row.name;
      const isQueued = queuedSet.has(row.name);
      // v0M6.3 — extended prereq: accept researched OR queued OR
      // active-parent so chains can be lined up in one session of
      // enqueue clicks. Mirrors sim-side enqueue validation.
      const parentActive = spec.prerequisite !== '' && active !== null && active.knowledgeName === spec.prerequisite;
      const parentQueued = spec.prerequisite !== '' && queuedSet.has(spec.prerequisite);
      const prereqMet =
        spec.prerequisite === '' ||
        researched.has(spec.prerequisite) ||
        parentActive ||
        parentQueued;
      const canAfford = learning >= spec.learningCost;

      if (isResearched) {
        row.statusIcon.textContent = '●';
        row.statusIcon.style.color = '#7aa668'; // green — done
        row.button.textContent = 'Researched';
        row.button.disabled = true;
        row.button.title = '';
      } else if (isActive) {
        row.statusIcon.textContent = '◐';
        row.statusIcon.style.color = '#f4d47a'; // gold — in progress
        row.button.textContent = 'Researching';
        row.button.disabled = true;
        row.button.title = '';
      } else if (isQueued) {
        const pos = queue.indexOf(row.name) + 1;
        row.statusIcon.textContent = '◑';
        row.statusIcon.style.color = '#c4a874'; // parchment — queued
        row.button.textContent = `Queued #${pos}`;
        row.button.disabled = true;
        row.button.title = 'Already in queue';
      } else if (!prereqMet) {
        row.statusIcon.textContent = '○';
        row.statusIcon.style.color = '#5a4a30'; // dim — locked
        row.button.textContent = 'Locked';
        row.button.disabled = true;
        row.button.title = `Requires: ${spec.prerequisite}`;
      } else if (queueFull) {
        row.statusIcon.textContent = '○';
        row.statusIcon.style.color = '#a3906a';
        row.button.textContent = 'Queue Full';
        row.button.disabled = true;
        row.button.title = `Queue cap: ${RESEARCH_QUEUE_CAP} items`;
      } else {
        // Reachable. Two display flavours:
        //   - Immediate-promotion case (nothing active, empty queue,
        //     can afford Learning right now) → "Research" — clicking
        //     enqueues + the sim promotes on next tick.
        //   - Queue case (something active OR queue non-empty OR
        //     can't currently afford) → "Queue" — enqueue and let the
        //     sim promote when it can.
        const willPromoteImmediately = active === null && queue.length === 0 && canAfford;
        row.statusIcon.textContent = '○';
        row.statusIcon.style.color = canAfford ? '#c4a874' : '#8a5a4a';
        if (willPromoteImmediately) {
          row.button.textContent = 'Research';
          row.button.title = '';
        } else {
          row.button.textContent = 'Queue';
          row.button.title = canAfford
            ? 'Add to queue'
            : `Queue now — Learning check runs at promotion (need ${spec.learningCost}, have ${learning})`;
        }
        row.button.disabled = false;
      }

      // v0M6.1 (2026-07-26) — colour the connector edge from parent to
      // this row. Green when this row is researched, gold when active,
      // parchment when queued or parent is done, dim otherwise.
      if (row.edgePath !== null) {
        let stroke = '#5a4a30'; // dim / locked default
        const parentResearched = spec.prerequisite !== '' && researched.has(spec.prerequisite);
        if (isResearched) {
          stroke = '#7aa668'; // green — completed chain
        } else if (isActive) {
          stroke = '#f4d47a'; // gold — active research target
        } else if (isQueued || parentResearched || parentActive || parentQueued) {
          stroke = '#a3906a'; // parchment — reachable / queued
        }
        row.edgePath.setAttribute('stroke', stroke);
      }
    }

    // Rewire button click handlers to close over the CURRENT playerFactionId.
    // We do this each sync to survive playerFactionId changes across
    // scenario restarts (rare but possible).
    // v0M6.3 (2026-07-26) — dispatch `enqueue_research` instead of
    // `start_research`. The sim's research system promotes queue-head
    // to active naturally on the next tick, so an empty queue + no
    // active research still starts immediately.
    for (const row of rows) {
      const capturedName = row.name;
      row.button.onclick = () => {
        callbacks.onIssue({
          type: 'enqueue_research',
          tick: state.tick,
          playerFactionId,
          knowledgeName: capturedName,
        });
      };
    }
  }

  function destroy(): void {
    window.removeEventListener('resize', onResize);
    backdrop.remove();
    popover.remove();
  }

  return { sync, destroy };
}
